import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiCheckCircle, 
  FiXCircle, 
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiEye,
  FiDownload,
  FiAlertCircle
} from 'react-icons/fi';
import BaseUrl from '../../Url/BaseUrl';
const BusServiceApprovalPage = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedForAction, setSelectedForAction] = useState(null);
  const navigate = useNavigate();  
  // Fetch pending approvals
  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BaseUrl.baseurl}Buses/GetPending`);
      setPendingApprovals(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch pending approvals. Please try again.');
      console.error('Error fetching pending approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle approval action for single item
  const handleApprove = async (bus) => {
    try {
      setActionLoading(true);
      setSelectedForAction(bus.busNo);
      
      // Prepare payload with status as true
      const payload = {
        ...bus,
        status: true
      };
      
      // Call API to update bus status
      const response = await axios.put(`${BaseUrl.baseurl}Buses/${bus.busNo}`, payload);
      
      if (response.status === 200) {
        // Remove approved item from list
        setPendingApprovals(prev => prev.filter(item => item.busNo !== bus.busNo));
        setSelectedItems(prev => prev.filter(id => id !== bus.busNo));
        alert(`Bus service #${bus.busNo} approved successfully!`);
      } else {
        alert(`Failed to approve bus service #${bus.busNo}`);
      }
    } catch (err) {
      alert('Failed to approve. Please try again.');
      console.error('Error approving bus service:', err);
    } finally {
      setActionLoading(false);
      setSelectedForAction(null);
    }
  };

  // Handle rejection action for single item
  const handleReject = async (bus) => {
    const reason = window.prompt('Please provide reason for rejection:');
    if (!reason || reason.trim() === '') {
      alert('Rejection reason is required.');
      return;
    }

    try {
      setActionLoading(true);
      setSelectedForAction(bus.busNo);
      
      // Prepare payload with status as false and add rejection reason
      const payload = {
        ...bus,
        status: false,
        rejectionReason: reason // Add rejection reason if your API supports it
      };
      
      // Call API to update bus status
      const response = await axios.put(`${BaseUrl.baseurl}Buses/${bus.busNo}`, payload);
      
      if (response.status === 200) {
        // Remove rejected item from list
        setPendingApprovals(prev => prev.filter(item => item.busNo !== bus.busNo));
        setSelectedItems(prev => prev.filter(id => id !== bus.busNo));
        alert(`Bus service #${bus.busNo} rejected.`);
      } else {
        alert(`Failed to reject bus service #${bus.busNo}`);
      }
    } catch (err) {
      alert('Failed to reject. Please try again.');
      console.error('Error rejecting bus service:', err);
    } finally {
      setActionLoading(false);
      setSelectedForAction(null);
    }
  };

  // Handle bulk approval
  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) {
      alert('Please select items to approve.');
      return;
    }
    
    const confirmed = window.confirm(`Approve ${selectedItems.length} selected item(s)?`);
    if (!confirmed) return;
    
    try {
      setActionLoading(true);
      
      // Get selected bus objects
      const selectedBuses = pendingApprovals.filter(bus => 
        selectedItems.includes(bus.busNo)
      );
      
      // Process each selected bus
      const approvalPromises = selectedBuses.map(async (bus) => {
        const payload = {
          ...bus,
          status: true
        };
        
        return axios.put(`${BaseUrl.baseurl}Buses/${bus.busNo}`, payload);
      });
      
      // Wait for all approvals to complete
      const results = await Promise.allSettled(approvalPromises);
      
      // Check results
      const successfulApprovals = results.filter(result => 
        result.status === 'fulfilled' && result.value.status === 200
      ).length;
      
      const failedApprovals = results.filter(result => 
        result.status === 'rejected' || (result.status === 'fulfilled' && result.value.status !== 200)
      ).length;
      
      // Remove successfully approved items
      const successfulBusNos = results
        .filter((result, index) => 
          result.status === 'fulfilled' && result.value.status === 200
        )
        .map((result, index) => selectedBuses[index].busNo);
      
      setPendingApprovals(prev => 
        prev.filter(item => !successfulBusNos.includes(item.busNo))
      );
      setSelectedItems([]);
      
      // Show summary
      if (failedApprovals === 0) {
        alert(`Successfully approved ${successfulApprovals} item(s)!`);
      } else {
        alert(`Approved ${successfulApprovals} item(s), ${failedApprovals} failed.`);
      }
      
    } catch (err) {
      alert('Failed to process bulk approval. Please try again.');
      console.error('Error in bulk approval:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk rejection
  const handleBulkReject = async () => {
    if (selectedItems.length === 0) {
      alert('Please select items to reject.');
      return;
    }
    
    const reason = window.prompt('Please provide reason for rejecting all selected items:');
    if (!reason || reason.trim() === '') {
      alert('Rejection reason is required.');
      return;
    }
    
    const confirmed = window.confirm(`Reject ${selectedItems.length} selected item(s)?`);
    if (!confirmed) return;
    
    try {
      setActionLoading(true);
      
      // Get selected bus objects
      const selectedBuses = pendingApprovals.filter(bus => 
        selectedItems.includes(bus.busNo)
      );
      
      // Process each selected bus
      const rejectionPromises = selectedBuses.map(async (bus) => {
        const payload = {
          ...bus,
          status: false,
          rejectionReason: reason
        };
        
        return axios.put(`${BaseUrl.baseurl}Buses/${bus.busNo}`, payload);
      });
      
      // Wait for all rejections to complete
      const results = await Promise.allSettled(rejectionPromises);
      
      // Check results
      const successfulRejections = results.filter(result => 
        result.status === 'fulfilled' && result.value.status === 200
      ).length;
      
      const failedRejections = results.filter(result => 
        result.status === 'rejected' || (result.status === 'fulfilled' && result.value.status !== 200)
      ).length;
      
      // Remove successfully rejected items
      const successfulBusNos = results
        .filter((result, index) => 
          result.status === 'fulfilled' && result.value.status === 200
        )
        .map((result, index) => selectedBuses[index].busNo);
      
      setPendingApprovals(prev => 
        prev.filter(item => !successfulBusNos.includes(item.busNo))
      );
      setSelectedItems([]);
      
      // Show summary
      if (failedRejections === 0) {
        alert(`Successfully rejected ${successfulRejections} item(s)!`);
      } else {
        alert(`Rejected ${successfulRejections} item(s), ${failedRejections} failed.`);
      }
      
    } catch (err) {
      alert('Failed to process bulk rejection. Please try again.');
      console.error('Error in bulk rejection:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(pendingApprovals.map(item => item.busNo));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle individual selection
  const handleSelectItem = (busNo) => {
    setSelectedItems(prev => 
      prev.includes(busNo) 
        ? prev.filter(id => id !== busNo)
        : [...prev, busNo]
    );
  };

  // View details modal
  const handleViewDetails = (bus) => {
    // Create a detailed view modal
    const details = `
Bus Number: #${bus.busNo}
Registration: ${bus.busRegistrationNumber || 'N/A'}
Bus Name: ${bus.busName || 'N/A'}
Operator Number: ${bus.operatorNumber || 'N/A'}
Route Path: ${bus.routePathNo || 'N/A'}
Contact: ${bus.busContactNumber || 'N/A'}
State: ${bus.stateCode || 'N/A'}
Zone: ${bus.zoneCode || 'N/A'}
Status: Pending Approval
    `;
    
    alert(details);
  };

  // Export to CSV
  const handleExport = () => {
    const headers = [
      'Bus No',
      'Registration No',
      'Bus Name',
      'Operator No',
      'Route Path',
      'Contact',
      'State',
      'Zone'
    ];
    
    const csvData = pendingApprovals.map(bus => [
      bus.busNo,
      bus.busRegistrationNumber || 'N/A',
      bus.busName || 'N/A',
      bus.operatorNumber || 'N/A',
      bus.routePathNo || 'N/A',
      bus.busContactNumber || 'N/A',
      bus.stateCode || 'N/A',
      bus.zoneCode || 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bus-approvals-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter approvals based on search term
  const filteredApprovals = pendingApprovals.filter(approval => {
    const searchLower = searchTerm.toLowerCase();
    return (
      approval.busNo.toString().includes(searchLower) ||
      (approval.busRegistrationNumber && approval.busRegistrationNumber.toLowerCase().includes(searchLower)) ||
      (approval.busName && approval.busName.toLowerCase().includes(searchLower)) ||
      (approval.operatorNumber && approval.operatorNumber.toLowerCase().includes(searchLower)) ||
      (approval.routePathNo && approval.routePathNo.toLowerCase().includes(searchLower))
    );
  });

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  return (
    <div className="approval-page">
      {/* Header */}
      <div className="approval-page__header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <div className="approval-page__title-section">
          <h1>Bus Services Approval Management</h1>
          <p>Review and approve pending bus service registrations</p>
        </div>
      </div>

      {/* Stats and Actions */}
      <div className="approval-page__stats">
        <div className="stats-card">
          <div className="stats-card__icon pending">
            <FiRefreshCw size="1.5em" />
          </div>
          <div className="stats-card__content">
            <h3>{pendingApprovals.length}</h3>
            <p>Pending Approvals</p>
          </div>
        </div>
        
        <div className="approval-page__actions">
          <div className="action-group">
            <button 
              className="btn btn-success"
              onClick={handleBulkApprove}
              disabled={selectedItems.length === 0 || actionLoading}
            >
              {actionLoading ? (
                <>
                  <div className="spinner-small"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FiCheckCircle /> Approve Selected ({selectedItems.length})
                </>
              )}
            </button>
            <button 
              className="btn btn-danger"
              onClick={handleBulkReject}
              disabled={selectedItems.length === 0 || actionLoading}
            >
              <FiXCircle /> Reject Selected
            </button>
          </div>
          <div className="action-group">
            <button 
              className="btn btn-primary"
              onClick={fetchPendingApprovals}
              disabled={actionLoading}
            >
              <FiRefreshCw /> Refresh
            </button>
            <button 
              className="btn btn-outline"
              onClick={handleExport}
              disabled={pendingApprovals.length === 0}
            >
              <FiDownload /> Export
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="approval-page__filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by bus number, name, registration..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-info">
          <span className="filter-badge">
            <FiFilter /> Showing {filteredApprovals.length} of {pendingApprovals.length} items
          </span>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading pending approvals...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-state">
          <FiAlertCircle size="2em" className="error-icon" />
          <p className="error-message">{error}</p>
          <button className="btn btn-retry" onClick={fetchPendingApprovals}>
            Retry
          </button>
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && filteredApprovals.length === 0 && (
        <div className="empty-state">
          <div className="empty-state__icon">
            <FiCheckCircle size="3em" />
          </div>
          <h3>No Pending Approvals</h3>
          <p>All bus service registrations have been processed.</p>
          <button className="btn btn-primary" onClick={fetchPendingApprovals}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      )}

      {/* Approvals Table */}
      {!loading && !error && filteredApprovals.length > 0 && (
        <div className="approvals-table-container">
          <div className="table-responsive">
            <table className="approvals-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedItems.length === pendingApprovals.length && pendingApprovals.length > 0}
                      disabled={actionLoading}
                    />
                  </th>
                  <th>Bus No</th>
                  <th>Registration No</th>
                  <th>Bus Name</th>
                  <th>Operator No</th>
                  <th>Route Path</th>
                  <th>Contact</th>
                  <th>State</th>
                  <th>Zone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApprovals.map((approval) => (
                  <tr key={approval.busNo} className="approval-row">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(approval.busNo)}
                        onChange={() => handleSelectItem(approval.busNo)}
                        disabled={actionLoading}
                      />
                    </td>
                    <td>
                      <span className="badge badge-primary">#{approval.busNo}</span>
                    </td>
                    <td>
                      <div className="cell-content">
                        <strong>{approval.busRegistrationNumber || 'N/A'}</strong>
                      </div>
                    </td>
                    <td>{approval.busName || 'N/A'}</td>
                    <td>{approval.operatorNumber || 'N/A'}</td>
                    <td>
                      <span className="badge badge-secondary">
                        {approval.routePathNo || 'N/A'}
                      </span>
                    </td>
                    <td>{approval.busContactNumber || 'N/A'}</td>
                    <td>
                      <span className="badge badge-outline">
                        {approval.stateCode || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-outline">
                        {approval.zoneCode || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-action btn-view"
                          onClick={() => handleViewDetails(approval)}
                          title="View Details"
                          disabled={actionLoading}
                        >
                          <FiEye />
                        </button>
                        <button
                          className="btn-action btn-approve"
                          onClick={() => handleApprove(approval)}
                          title="Approve"
                          disabled={actionLoading}
                        >
                          {actionLoading && selectedForAction === approval.busNo ? (
                            <div className="spinner-tiny"></div>
                          ) : (
                            <FiCheckCircle />
                          )}
                        </button>
                        <button
                          className="btn-action btn-reject"
                          onClick={() => handleReject(approval)}
                          title="Reject"
                          disabled={actionLoading}
                        >
                          <FiXCircle />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Selected Items Summary */}
          {selectedItems.length > 0 && (
            <div className="selected-summary">
              <div className="selected-count">
                <FiCheckCircle /> {selectedItems.length} item(s) selected
              </div>
              <div className="selected-actions">
                <button
                  className="btn btn-sm btn-success"
                  onClick={handleBulkApprove}
                  disabled={actionLoading}
                >
                  Approve All
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleBulkReject}
                  disabled={actionLoading}
                >
                  Reject All
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CSS Styles */}
      <style jsx>{`
        .approval-page {
          padding: 2rem;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .approval-page__header {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .btn-back {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
          color: #555;
          transition: all 0.3s ease;
        }

        .btn-back:hover {
          background: #f5f5f5;
          border-color: #ddd;
        }

        .approval-page__title-section h1 {
          margin: 0;
          font-size: 1.8rem;
          color: #333;
        }

        .approval-page__title-section p {
          margin: 0.5rem 0 0;
          color: #666;
          font-size: 0.95rem;
        }

        .approval-page__stats {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          margin-bottom: 1.5rem;
        }

        .stats-card {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .stats-card__icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stats-card__icon.pending {
          background: #fff3e0;
          color: #f57c00;
        }

        .stats-card__content h3 {
          margin: 0;
          font-size: 1.8rem;
          color: #333;
        }

        .stats-card__content p {
          margin: 0.25rem 0 0;
          color: #666;
          font-size: 0.9rem;
        }

        .approval-page__actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .action-group {
          display: flex;
          gap: 0.75rem;
        }

        .btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          min-width: 120px;
        }

        .btn-sm {
          padding: 0.5rem 1rem;
          font-size: 0.85rem;
          min-width: auto;
        }

        .btn-success {
          background: #4CAF50;
          color: white;
        }

        .btn-success:hover:not(:disabled) {
          background: #43a047;
        }

        .btn-success:disabled {
          background: #a5d6a7;
          cursor: not-allowed;
        }

        .btn-danger {
          background: #f44336;
          color: white;
        }

        .btn-danger:hover:not(:disabled) {
          background: #d32f2f;
        }

        .btn-danger:disabled {
          background: #ef9a9a;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #2196F3;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #1976D2;
        }

        .btn-outline {
          background: white;
          border: 1px solid #ddd;
          color: #555;
        }

        .btn-outline:hover:not(:disabled) {
          background: #f5f5f5;
        }

        .btn-outline:disabled {
          background: #f5f5f5;
          color: #aaa;
          cursor: not-allowed;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .spinner-tiny {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(76, 175, 80, 0.3);
          border-top: 2px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .approval-page__filters {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          margin-bottom: 1.5rem;
        }

        .search-box {
          position: relative;
          width: 300px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: border-color 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #4CAF50;
        }

        .filter-info {
          display: flex;
          align-items: center;
        }

        .filter-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: #f0f0f0;
          border-radius: 20px;
          font-size: 0.85rem;
          color: #666;
        }

        .loading-state,
        .error-state,
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .error-icon {
          color: #f44336;
          margin-bottom: 1rem;
        }

        .error-message {
          color: #f44336;
          margin-bottom: 1rem;
        }

        .btn-retry {
          background: #2196F3;
          color: white;
          padding: 0.75rem 2rem;
          border-radius: 8px;
          border: none;
          cursor: pointer;
        }

        .empty-state__icon {
          color: #4CAF50;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          margin: 0 0 0.5rem;
          color: #333;
        }

        .empty-state p {
          color: #666;
          margin-bottom: 1.5rem;
        }

        .approvals-table-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .table-responsive {
          overflow-x: auto;
        }

        .approvals-table {
          width: 100%;
          border-collapse: collapse;
        }

        .approvals-table th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #555;
          border-bottom: 2px solid #eee;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .approvals-table td {
          padding: 1rem;
          border-bottom: 1px solid #eee;
          color: #333;
          font-size: 0.9rem;
        }

        .approvals-table tr:hover {
          background: #f9f9f9;
        }

        .approvals-table input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .badge-primary {
          background: #e3f2fd;
          color: #1976D2;
        }

        .badge-secondary {
          background: #f3e5f5;
          color: #7b1fa2;
        }

        .badge-outline {
          background: white;
          border: 1px solid #ddd;
          color: #666;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .btn-action {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .btn-action:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-view {
          background: #e3f2fd;
          color: #1976D2;
        }

        .btn-view:hover:not(:disabled) {
          background: #bbdefb;
        }

        .btn-approve {
          background: #e8f5e9;
          color: #4CAF50;
        }

        .btn-approve:hover:not(:disabled) {
          background: #c8e6c9;
        }

        .btn-reject {
          background: #ffebee;
          color: #f44336;
        }

        .btn-reject:hover:not(:disabled) {
          background: #ffcdd2;
        }

        .selected-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: #e8f5e9;
          border-top: 1px solid #c8e6c9;
        }

        .selected-count {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #2e7d32;
          font-weight: 500;
        }

        .selected-actions {
          display: flex;
          gap: 0.75rem;
        }

        .cell-content {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .approval-page {
            padding: 1rem;
          }

          .approval-page__stats {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .approval-page__actions {
            width: 100%;
          }

          .action-group {
            flex-wrap: wrap;
          }

          .btn {
            flex: 1;
            min-width: auto;
          }

          .approval-page__filters {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .search-box {
            width: 100%;
          }

          .selected-summary {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .selected-actions {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default BusServiceApprovalPage;